import { Fragment } from "react";
import { Check, Circle, CornerDownRight } from "lucide-react";

/*
  FUNCTIONAL UNIT MAP — the student's writing SEEN FUNCTIONALLY (Increment 3).
  Purely presentational projection of visual_map.functional_map (backend _vm_functional_map): the DCO's functional-unit
  reading — what this part is doing, its place in the larger whole, the functional MOVEMENTS it actually makes (content +
  function together), what is carried by what, and the one movement being worked on now. A movement is a functional
  movement in the writing, not a chunk of prose. No five-slot template, no controls, no pedagogy decided here.
*/
const TERRA = "#8C3A2A";
const REGION = { introduction: "Introduction", body: "Body", conclusion: "Conclusion" };
const OPEN_LABEL = "Needed · not yet developed";

function Mark({ state }) {
  const base = "flex h-5 w-5 shrink-0 items-center justify-center";
  if (state === "done")
    return (
      <span className={`${base} rounded-full bg-emerald-100 text-emerald-700`} data-testid="map-mark-done" aria-label="in place">
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  if (state === "current")
    return (
      <span className={`${base} rounded-full bg-white`} style={{ color: TERRA }} data-testid="map-mark-current" aria-label="working on this now">
        <span className="h-2.5 w-2.5 rounded-full bg-current" />
      </span>
    );
  return (
    <span className={`${base} text-stone-400`} data-testid="map-mark-open" aria-label="needed, not yet developed">
      <Circle className="h-3 w-3" strokeWidth={2} />
    </span>
  );
}

const pmark = (where) => (Array.isArray(where) && where.length ? where.map((p) => `¶${p + 1}`).join(" ") : "");

function Movement({ mv, i, cur, target, branch, compact }) {
  const fnLine = (mv.functions || []).join(" · ");
  const carries = (mv.carries || []).join(", ");
  const loc = pmark(mv.where);
  // ONE current-target cue: the full map fills the current row; the compact map (the focused workspace) only tints it and keeps the pill
  const filled = cur && !compact;
  const style = filled ? { background: TERRA, borderColor: TERRA } : cur ? { borderColor: TERRA, background: "rgba(140,58,42,0.06)" } : {};
  const cls = filled ? "text-white shadow-md" : cur ? "" : mv.state === "done" ? "border-emerald-200 bg-emerald-50/50" : "border-dashed border-stone-300 bg-white";
  return (
    <li data-testid={`fm-move-${i}`} data-node-state={mv.state} aria-current={cur ? "step" : undefined}
      className={`rounded-xl border px-3.5 ${compact ? "py-2" : "py-2.5"} ${cls}`} style={style}>
      <div className="flex items-start gap-3">
        <Mark state={mv.state} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className={`text-[13px] leading-snug ${filled ? "text-white" : mv.state === "open" ? "italic text-stone-500" : "text-stone-800"}`} data-testid={`fm-move-content-${i}`}>
              {mv.content}
            </p>
            {cur ? (
              <span data-testid="map-you-are-here" className={`inline-flex items-center gap-1.5 rounded-full px-2 py-[2px] text-[9px] font-bold uppercase tracking-[0.18em] ${filled ? "bg-white" : "bg-white border border-[#8C3A2A]/30"}`} style={{ color: TERRA }}>
                <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> You are here
              </span>
            ) : mv.state === "open" ? (
              <span data-testid={`fm-move-open-${i}`} className="text-[9px] font-bold uppercase tracking-[0.12em] text-stone-400">{OPEN_LABEL}</span>
            ) : null}
          </div>
          <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${filled ? "text-white/85" : "text-stone-500"}`}>
            <span data-testid={`fm-move-functions-${i}`}>{fnLine}</span>
            {carries ? (
              <span data-testid={`fm-move-carries-${i}`} className={`inline-flex items-center gap-1 normal-case tracking-normal font-medium ${filled ? "text-white/80" : "text-stone-500"}`}>
                <CornerDownRight className="h-3 w-3" strokeWidth={2.5} /> also carries your {carries.toLowerCase()}
              </span>
            ) : null}
            {loc ? <span data-testid={`fm-move-where-${i}`} className={`ml-auto font-mono font-medium tracking-normal ${filled ? "text-white/70" : "text-stone-400"}`}>{loc}</span> : null}
          </div>
          {cur && target && target.develops ? (
            compact ? (
              <p className="mt-1 text-[11px] leading-snug text-stone-500" data-testid="fm-target-develops">↳ <span className="italic">{target.develops}</span></p>
            ) : (
              <p className="mt-1.5 text-[12px] leading-snug text-white/90" data-testid="fm-target-develops">
                Developing: <span className="italic">{target.develops}</span>
              </p>
            )
          ) : null}
        </div>
      </div>
      {branch}
    </li>
  );
}

export function FunctionalUnitMap({ fm, compact = false, branch = null }) {
  const unit = fm.unit || {};
  const movements = Array.isArray(fm.movements) ? fm.movements : [];
  const target = fm.target || null;
  const whole = fm.whole || null;
  const region = REGION[unit.region] || "";
  const wholeLine = whole ? (whole.role || whole.reader_need || whole.meaning || "") : "";
  const wholeMeaning = whole && whole.meaning && whole.meaning !== wholeLine ? whole.meaning : "";
  // COMPACT (the focused workspace): recognition and orientation only — the student's movements, the current target, the function
  // label. Region / span (the location line above already says it) and the whole→unit relation stay in the full map; "what this part
  // is doing" becomes one quiet line.
  return (
    <div data-testid="functional-map" data-region={unit.region || ""} data-span={unit.span || ""} data-movements={movements.length} data-compact={compact ? "1" : undefined}>
      {!compact && (region || unit.span) ? (
        <p data-testid="fm-region" className="mb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-400">
          {[region, unit.span].filter(Boolean).join(" · ")}
        </p>
      ) : null}
      {unit.doing ? (
        compact ? (
          <p className="mb-1.5 text-[12px] italic leading-snug text-stone-500" data-testid="fm-unit-doing">{unit.doing}</p>
        ) : (
          <div className="mb-2" data-testid="fm-unit">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">What this part is doing</p>
            <p className="mt-0.5 text-[13.5px] font-medium leading-snug text-stone-800" data-testid="fm-unit-doing">{unit.doing}</p>
          </div>
        )
      ) : null}
      {!compact && wholeLine ? (
        <div className="mb-2 flex items-start gap-2 text-[12px] leading-snug text-stone-500" data-testid="fm-whole">
          <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" strokeWidth={2.5} />
          <p>
            <span className="font-semibold uppercase tracking-[0.12em] text-[10px] text-stone-400">For the whole · </span>
            <span data-testid="fm-whole-role">{wholeLine}</span>
            {wholeMeaning ? <span className="text-stone-400" data-testid="fm-whole-meaning"> — “{wholeMeaning}”</span> : null}
          </p>
        </div>
      ) : null}
      <ol data-testid="map-structure" className="space-y-0.5">
        {movements.map((mv, i) => {
          const cur = mv.state === "current";
          return (
            <Fragment key={i}>
              {i > 0 ? (
                <div className="flex justify-center py-px" aria-hidden><span className="text-[13px] leading-none text-stone-400">↓</span></div>
              ) : null}
              <Movement mv={mv} i={i} cur={cur} target={target} branch={cur ? branch : null} compact={compact} />
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}

export const fmCurrent = (fm) => (fm && Array.isArray(fm.movements) ? fm.movements.find((m) => m.state === "current") || null : null);
